/* GW · Logo 3D
   Visor WebGL minimo para un .glb de una sola malla sin texturas.
   Se hace a mano en lugar de cargar model-viewer (casi 1 MB) porque
   esto es lo primero que ve quien entra en la tienda. */
(() => {
  const cajas = document.querySelectorAll('[data-gw-logo3d]');
  if (!cajas.length) return;

  // ---------- glTF binario: solo posiciones e indices ----------
  function leerGlb(buffer) {
    const dv = new DataView(buffer);
    if (dv.getUint32(0, true) !== 0x46546c67) throw new Error('no es un .glb');

    let off = 12;
    let json = null;
    let bin = null;
    while (off < dv.byteLength) {
      const largo = dv.getUint32(off, true);
      const tipo = dv.getUint32(off + 4, true);
      const datos = new Uint8Array(buffer, off + 8, largo);
      if (tipo === 0x4e4f534a) json = JSON.parse(new TextDecoder().decode(datos));
      if (tipo === 0x004e4942) bin = buffer.slice(off + 8, off + 8 + largo);
      off += 8 + largo;
    }
    if (!json || !bin) throw new Error('.glb incompleto');

    const prim = json.meshes[0].primitives[0];
    const acPos = json.accessors[prim.attributes.POSITION];
    const acIdx = json.accessors[prim.indices];

    const trozo = (ac, Tipo, comp) => {
      const bv = json.bufferViews[ac.bufferView];
      const ini = (bv.byteOffset || 0) + (ac.byteOffset || 0);
      return new Tipo(bin, ini, ac.count * comp);
    };

    const pos = trozo(acPos, Float32Array, 3);
    const TipoIdx = acIdx.componentType === 5125 ? Uint32Array
      : acIdx.componentType === 5123 ? Uint16Array : Uint8Array;
    const idx = trozo(acIdx, TipoIdx, 1);

    const mueve = (json.nodes && json.nodes[0] && json.nodes[0].translation) || [0, 0, 0];
    return { pos, idx, mueve, min: acPos.min, max: acPos.max };
  }

  // ---------- malla desindexada con normal por cara ----------
  function preparar({ pos, idx, mueve }) {
    const n = idx.length;
    const vert = new Float32Array(n * 3);
    const norm = new Float32Array(n * 3);

    for (let t = 0; t < n; t += 3) {
      const a = idx[t] * 3;
      const b = idx[t + 1] * 3;
      const c = idx[t + 2] * 3;

      const ax = pos[a] + mueve[0], ay = pos[a + 1] + mueve[1], az = pos[a + 2] + mueve[2];
      const bx = pos[b] + mueve[0], by = pos[b + 1] + mueve[1], bz = pos[b + 2] + mueve[2];
      const cx = pos[c] + mueve[0], cy = pos[c + 1] + mueve[1], cz = pos[c + 2] + mueve[2];

      let nx = (by - ay) * (cz - az) - (bz - az) * (cy - ay);
      let ny = (bz - az) * (cx - ax) - (bx - ax) * (cz - az);
      let nz = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
      const largo = Math.hypot(nx, ny, nz);
      if (largo > 1e-12) {
        nx /= largo; ny /= largo; nz /= largo;
      } else {
        // triangulo degenerado: normal nula haria NaN en el shader
        nx = 0; ny = 0; nz = 1;
      }

      const o = t * 3;
      vert[o] = ax; vert[o + 1] = ay; vert[o + 2] = az;
      vert[o + 3] = bx; vert[o + 4] = by; vert[o + 5] = bz;
      vert[o + 6] = cx; vert[o + 7] = cy; vert[o + 8] = cz;
      for (let k = 0; k < 3; k++) {
        norm[o + k * 3] = nx; norm[o + k * 3 + 1] = ny; norm[o + k * 3 + 2] = nz;
      }
    }
    return { vert, norm, cuantos: n };
  }

  const VS = `#version 300 es
    in vec3 posicion;
    in vec3 normal;
    uniform mat4 proyeccion;
    uniform mat4 vista;
    uniform mat3 giro;
    out vec3 vNormal;
    out vec3 vHacia;
    void main() {
      vec3 p = giro * posicion;
      vNormal = normalize(giro * normal);
      vec4 ojo = vista * vec4(p, 1.0);
      vHacia = normalize(-ojo.xyz);
      gl_Position = proyeccion * ojo;
    }`;

  const FS = `#version 300 es
    precision highp float;
    in vec3 vNormal;
    in vec3 vHacia;
    uniform vec3 base;
    uniform vec3 tinte;
    uniform float metal;
    uniform float rugosidad;
    uniform float exposicion;
    out vec4 color;

    void main() {
      vec3 n = normalize(vNormal);
      if (!gl_FrontFacing) n = -n;
      vec3 v = normalize(vHacia);

      vec3 luces[3];
      luces[0] = normalize(vec3(-0.45, 0.75, 0.62));
      luces[1] = normalize(vec3( 0.80,-0.20, 0.45));
      luces[2] = normalize(vec3( 0.10, 0.25,-0.90));
      vec3 tonos[3];
      tonos[0] = vec3(1.00, 0.98, 0.95);
      tonos[1] = vec3(0.55, 0.68, 0.95);
      tonos[2] = vec3(0.75, 0.85, 1.00);
      float fuerza[3];
      fuerza[0] = 1.25; fuerza[1] = 0.55; fuerza[2] = 0.75;

      float brillo = 2.0 + (1.0 - rugosidad) * 120.0;
      vec3 acumulado = base * 0.06;

      for (int i = 0; i < 3; i++) {
        float difusa = max(dot(n, luces[i]), 0.0);
        vec3 media = normalize(luces[i] + v);
        float especular = pow(max(dot(n, media), 0.0), brillo);
        acumulado += tonos[i] * fuerza[i] *
          (base * difusa * (1.0 - metal * 0.55) + especular * (0.25 + metal));
      }

      float fresnel = pow(1.0 - max(dot(n, v), 0.0), 3.0);
      acumulado += fresnel * tinte * (0.35 + metal * 0.5);

      acumulado = pow(clamp(acumulado * exposicion, 0.0, 1.0), vec3(1.0 / 2.2));
      color = vec4(acumulado, 1.0);
    }`;

  function compilar(gl, tipo, fuente) {
    const s = gl.createShader(tipo);
    gl.shaderSource(s, fuente);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      throw new Error(gl.getShaderInfoLog(s) || 'shader');
    }
    return s;
  }

  const aRgb = (css) => {
    const t = (css || '').trim();
    const hex = t.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (hex) {
      const h = hex[1].length === 3 ? hex[1].replace(/./g, (c) => c + c) : hex[1];
      const n = parseInt(h, 16);
      return [(n >> 16 & 255) / 255, (n >> 8 & 255) / 255, (n & 255) / 255];
    }
    const rgb = t.match(/rgba?\(([^)]+)\)/i);
    if (rgb) {
      const p = rgb[1].split(',').map((v) => parseFloat(v));
      if (p.length >= 3) return [p[0] / 255, p[1] / 255, p[2] / 255];
    }
    return [1, 1, 1];
  };

  function arrancar(caja) {
    const lienzo = caja.querySelector('canvas');
    const url = caja.dataset.gwLogo3d;
    if (!lienzo || !url) return;

    const gl = lienzo.getContext('webgl2', { antialias: true, alpha: true });
    if (!gl) { caja.classList.add('gwp3d--sin'); return; }

    const num = (n, x) => {
      const v = parseFloat(caja.dataset[n]);
      return Number.isFinite(v) ? v : x;
    };
    const cfg = {
      base: aRgb(caja.dataset.color || '#ffffff'),
      tinte: aRgb(caja.dataset.tinte || '#7398e0'),
      metal: num('metal', 85) / 100,
      rugosidad: num('rugosidad', 25) / 100,
      exposicion: num('exposicion', 115) / 100,
      velocidad: num('velocidad', 22) / 100,
      incl: num('inclinacion', 0) * Math.PI / 180,
      giro0: num('giroInicial', 25) * Math.PI / 180,
      amplitud: num('amplitud', 22) * Math.PI / 180,
      zoom: num('zoom', 100) / 100,
      modo: caja.dataset.modo || 'balanceo',
      auto: caja.dataset.auto !== 'no',
      arrastrar: caja.dataset.arrastrar !== 'no',
    };

    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.arrayBuffer();
      })
      .then((buf) => {
        const crudo = leerGlb(buf);
        const malla = preparar(crudo);

        const programa = gl.createProgram();
        gl.attachShader(programa, compilar(gl, gl.VERTEX_SHADER, VS));
        gl.attachShader(programa, compilar(gl, gl.FRAGMENT_SHADER, FS));
        gl.linkProgram(programa);
        if (!gl.getProgramParameter(programa, gl.LINK_STATUS)) {
          throw new Error(gl.getProgramInfoLog(programa) || 'programa');
        }
        gl.useProgram(programa);

        const vao = gl.createVertexArray();
        gl.bindVertexArray(vao);
        for (const [nombre, datos] of [['posicion', malla.vert], ['normal', malla.norm]]) {
          const buffer = gl.createBuffer();
          gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
          gl.bufferData(gl.ARRAY_BUFFER, datos, gl.STATIC_DRAW);
          const loc = gl.getAttribLocation(programa, nombre);
          gl.enableVertexAttribArray(loc);
          gl.vertexAttribPointer(loc, 3, gl.FLOAT, false, 0, 0);
        }

        const u = (n) => gl.getUniformLocation(programa, n);
        gl.uniform3fv(u('base'), cfg.base);
        gl.uniform3fv(u('tinte'), cfg.tinte);
        gl.uniform1f(u('metal'), cfg.metal);
        gl.uniform1f(u('rugosidad'), cfg.rugosidad);
        gl.uniform1f(u('exposicion'), cfg.exposicion);

        gl.enable(gl.DEPTH_TEST);

        // Encuadre. Se usa proyeccion ortografica a proposito: con perspectiva,
        // al girar, el extremo que se acerca crece y arrastra el centro hasta un
        // cuarto de pantalla. En ortografica el centro se queda clavado.
        const mx = Math.max(Math.abs(crudo.min[0]), Math.abs(crudo.max[0]));
        const my = Math.max(Math.abs(crudo.min[1]), Math.abs(crudo.max[1]));
        const mz = Math.max(Math.abs(crudo.min[2]), Math.abs(crudo.max[2]));
        // cotas validas para cualquier angulo: asi el tamano en pantalla no cambia
        const radioX = mx + mz;
        const radioY = my + mz;

        let giro = cfg.giro0;
        let incl = cfg.incl;
        let manual = 0;

        function medir() {
          const r = lienzo.getBoundingClientRect();
          const dpr = Math.min(window.devicePixelRatio || 1, 2);
          const anchoCss = Math.max(r.width, 1);
          const altoCss = Math.max(r.height, 1);
          lienzo.width = Math.round(anchoCss * dpr);
          lienzo.height = Math.round(altoCss * dpr);
          gl.viewport(0, 0, lienzo.width, lienzo.height);

          const aspecto = anchoCss / altoCss;
          const lejos = 100;
          const cerca = 0.1;

          const mediaAncho = Math.max(radioX, radioY * aspecto) * 1.06 / cfg.zoom;
          const mediaAlto = mediaAncho / aspecto;

          gl.uniformMatrix4fv(u('proyeccion'), false, new Float32Array([
            1 / mediaAncho, 0, 0, 0,
            0, 1 / mediaAlto, 0, 0,
            0, 0, -2 / (lejos - cerca), 0,
            0, 0, -(lejos + cerca) / (lejos - cerca), 1,
          ]));

          gl.uniformMatrix4fv(u('vista'), false, new Float32Array([
            1, 0, 0, 0,
            0, 1, 0, 0,
            0, 0, 1, 0,
            0, 0, -10, 1,
          ]));
        }

        let arrastrando = false;
        let marco = 0;
        let visible = false;
        let x0 = 0;
        let y0 = 0;
        let anterior = 0;
        let reloj = 0;

        function pintar(ahora) {
          const salto = anterior ? Math.min((ahora - anterior) / 1000, 0.1) : 0.016;
          anterior = ahora;

          if (cfg.auto && !arrastrando) reloj += cfg.velocidad * salto;
          if (cfg.modo === 'vuelta') {
            giro = cfg.giro0 + manual + reloj;
          } else if (cfg.modo === 'balanceo') {
            giro = cfg.giro0 + manual + Math.sin(reloj) * cfg.amplitud;
          } else {
            giro = cfg.giro0 + manual;
          }
          const cy = Math.cos(giro), sy = Math.sin(giro);
          const cx = Math.cos(incl), sx = Math.sin(incl);
          // giro en Y y luego inclinacion en X, en columnas para GLSL
          gl.uniformMatrix3fv(u('giro'), false, new Float32Array([
            cy, sx * sy, -cx * sy,
            0, cx, sx,
            sy, -sx * cy, cx * cy,
          ]));
          gl.clearColor(0, 0, 0, 0);
          gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
          gl.drawArrays(gl.TRIANGLES, 0, malla.cuantos);
          if (visible) marco = requestAnimationFrame(pintar);
        }

        if (cfg.arrastrar) {
          lienzo.style.cursor = 'grab';
          lienzo.addEventListener('pointerdown', (e) => {
            arrastrando = true; x0 = e.clientX; y0 = e.clientY;
            lienzo.setPointerCapture(e.pointerId);
            lienzo.style.cursor = 'grabbing';
          });
          lienzo.addEventListener('pointermove', (e) => {
            if (!arrastrando) return;
            manual += (e.clientX - x0) * 0.01;
            incl = Math.max(-1.2, Math.min(1.2, incl + (e.clientY - y0) * 0.006));
            x0 = e.clientX; y0 = e.clientY;
          });
          const soltar = () => { arrastrando = false; lienzo.style.cursor = 'grab'; };
          lienzo.addEventListener('pointerup', soltar);
          lienzo.addEventListener('pointercancel', soltar);
        }

        const arranca = () => {
          if (visible) return;
          visible = true;
          medir();
          marco = requestAnimationFrame(pintar);
        };
        const para = () => {
          visible = false;
          anterior = 0;
          cancelAnimationFrame(marco);
        };

        new ResizeObserver(() => { if (visible) medir(); }).observe(lienzo);
        new IntersectionObserver((e) => {
          e[0].isIntersecting ? arranca() : para();
        }, { threshold: 0.01 }).observe(lienzo);

        caja.classList.add('gwp3d--listo');
      })
      .catch((err) => {
        console.warn('[GW] logo 3D:', err.message);
        caja.classList.add('gwp3d--sin');
      });
  }

  cajas.forEach(arrancar);
})();